/**
 * IAB Taxonomy Classifier Workflow
 *
 * 1:1 Port of Python workflow from:
 * src/email_parser/workflow/graph.py (lines 1-313)
 *
 * 6-Node LangGraph workflow for IAB classification:
 *   1. load_emails - Load and filter new emails
 *   2. retrieve_profile - Get existing IAB profile with temporal decay
 *   3. analyze_all - Run all 4 analyzer agents (demographics, household, interests, purchase)
 *   4. reconcile - Merge classifications and resolve conflicts
 *   5. update_memory - Persist to IndexedDBStore
 *   6. advance_email - Move to next email (loops back to retrieve_profile)
 *
 * Usage:
 * ```typescript
 * import { buildWorkflowGraph } from '@browser/agents/iab-classifier'
 * import { IndexedDBStore } from '@browser/store'
 * import { createCheckpointer } from '@browser/checkpointer'
 *
 * const store = new IndexedDBStore()
 * const checkpointer = await createCheckpointer() // optional
 * const classifier = buildWorkflowGraph(store, checkpointer)
 *
 * const result = await classifier.invoke({
 *   user_id: 'user_123',
 *   emails: [{ id: 'email_1', subject: '...', body: '...' }],
 *   llm_provider: 'openai',
 *   llm_model: 'gpt-4o'
 * })
 * ```
 */
import { StateGraph, END } from '@langchain/langgraph/web'; // Browser-compatible import
import { WorkflowState, hasMoreEmails, advanceToNextEmail } from './state'; // Python line 34
import { loadNewEmailsNode } from './nodes/loadEmails'; // Python line 37
import { retrieveExistingProfileNode } from './nodes/retrieveProfile'; // Python line 38
import { analyzeAllNode } from './analyzers'; // Python line 39
import { reconcileEvidenceNode } from './nodes/reconcile'; // Python line 40
import { updateMemoryNode } from './nodes/updateMemory'; // Python line 41
import { MemoryManager } from './memory/MemoryManager'; // Python line 16
// Re-export WorkflowState for external use
export { WorkflowState } from './state';
// Re-export profile tier formatter functions and types
export { formatTieredDemographics, formatTieredHousehold, formatTieredInterests, formatTieredPurchaseIntent, addTieredStructureToProfile, } from './profileTierFormatter';
// Re-export tier selection functions
export { applyTieredClassification, calculateTierDepth, } from './tierSelector';
// Re-export canonical demographic key schema (Slice 0 / AT-01).
// Single source of truth for the wire-builder
// (apps/consumer/src/services/extension-credential-sync.ts), the G3 alignment
// test (@ownyou/bbs-plus), and the profileTierFormatter — all read from this
// one symbol. Per KAD-0.5, demographics + household merge into a single map;
// household routes into `claimSet.demographics` on the wire.
export { groupingToField, CANONICAL_DEMOGRAPHIC_KEYS, } from './canonical-demographic-keys';
// Re-export canonical demographic value schemas (Slice 0 / AT-14 + AT-15).
// Authoring source-of-truth for the per-key value contract (ADR 0005 Q3);
// the wallet's runtime contract is the synced policy artefact, not this
// module (JSON-can't-reference-TS rule). G3 (AT-21) enforces equivalence.
export { CANONICAL_DEMOGRAPHIC_VALUE_SCHEMAS, } from './canonical-demographic-values';
// Re-export the classifier-side normalizer (Slice 0 / AT-15).
// Deterministic semantic-resolution gate per ADR 0005 Q4: turns raw LLM
// output into canonical values, with structured drops on failure. The
// SINGLE semantic-resolution surface — downstream code (reconciler,
// wire-builder, profileTierFormatter, wallet) MUST NOT re-normalize.
export { normalizeUserAttribute, AT15_AUTHORED_KEYS, } from './normalize-user-attribute';
// Re-export CostTracker for A/B testing
export { CostTracker } from './costTracker';
export { RouteBackedAnalyzerLLMClient, parseLLMJsonResponse, } from './llm/client';
/**
 * Build compiled StateGraph for IAB Taxonomy Profile workflow.
 *
 * Python source: graph.py:48-147 (build_workflow_graph)
 *
 * @param store BaseStore instance for persistent storage
 * @param checkpointer Optional checkpointer for workflow state persistence (default: null)
 * @param userId Optional user_id for MemoryManager (extracted from state if not provided)
 * @returns Compiled StateGraph ready for execution
 *
 * Example:
 *   // Production (no checkpointing, maximum privacy)
 *   const store = new IndexedDBStore()
 *   const graph = buildWorkflowGraph(store)
 *   const result = await graph.invoke({ user_id: "user_123", emails: [...] })
 *
 *   // Development (PGlite checkpointing for debugging)
 *   const checkpointer = await createCheckpointer()
 *   const graph = buildWorkflowGraph(store, checkpointer)
 *   const result = await graph.invoke({ user_id: "user_123", emails: [...] })
 */
export function buildWorkflowGraph(// Python line 48
store, // Python line 49
checkpointer = null, // Python line 50
userId = 'default_user' // For MemoryManager
) {
    // Initialize StateGraph with WorkflowState schema                            // Python line 86
    const workflow = new StateGraph(WorkflowState); // Python line 87
    // Python line 76: Create MemoryManager wrapper around store
    // Note: In Python, user_id comes from state. Here we create a closure that extracts it.
    // The MemoryManager is created per-invocation to support different users.
    // Bind MemoryManager to nodes using closures                                 // Python lines 89-93
    const load_emails = async (state) => {
        const memoryManager = new MemoryManager(state.user_id || userId, store);
        return loadNewEmailsNode(state, memoryManager);
    };
    const retrieve_profile = async (state) => {
        const memoryManager = new MemoryManager(state.user_id || userId, store);
        return retrieveExistingProfileNode(state, memoryManager);
    };
    const reconcile = async (state) => {
        const memoryManager = new MemoryManager(state.user_id || userId, store);
        return reconcileEvidenceNode(state, memoryManager);
    };
    const update_memory = async (state) => {
        const memoryManager = new MemoryManager(state.user_id || userId, store);
        return updateMemoryNode(state, memoryManager);
    };
    // Add nodes to graph                                                         // Python line 95
    workflow.addNode('load_emails', load_emails); // Python line 96
    workflow.addNode('retrieve_profile', retrieve_profile); // Python line 97
    workflow.addNode('analyze_all', analyzeAllNode); // Python line 98
    workflow.addNode('reconcile', reconcile); // Python line 99
    workflow.addNode('update_memory', update_memory); // Python line 100
    // REMOVED: advance_email node (no longer needed - batch processing mode)
    // Define edges                                                               // Python line 103
    workflow.setEntryPoint('load_emails'); // Python line 104
    // After loading, check if we have emails to process                          // Python line 106
    workflow.addConditionalEdges(// Python line 107
    'load_emails', // Python line 108
    _checkHasEmailsConditional, // Python line 109
    {
        has_emails: 'retrieve_profile', // Python line 111
        no_emails: END, // Python line 112
    });
    // After retrieving profile, run all analyzers                                // Python line 116
    // (analyzeAllNode internally calls all 4 analyzer functions)                 // Python line 117
    workflow.addEdge('retrieve_profile', 'analyze_all'); // Python line 118
    // Analyzers flow to reconciliation                                           // Python line 120
    workflow.addEdge('analyze_all', 'reconcile'); // Python line 121
    // Reconciliation flows to memory update                                      // Python line 123
    workflow.addEdge('reconcile', 'update_memory'); // Python line 124
    // After updating memory, END workflow (batch processing complete)            // MODIFIED: No loop back
    // In batch processing mode, the entire batch is processed in ONE pass
    // The frontend workflow loop handles sending multiple batches sequentially
    workflow.addEdge('update_memory', END); // Python line 132
    // Compile graph (with optional checkpointer)                                 // Python line 139
    if (checkpointer) { // Python line 140
        const compiled_graph = workflow.compile({ checkpointer }); // Python line 141
        console.info('Workflow graph compiled successfully with checkpointing enabled'); // Python line 142
        return compiled_graph; // Python line 143
    }
    else { // Python line 144
        const compiled_graph = workflow.compile(); // Python line 145
        console.info('Workflow graph compiled successfully (no checkpointing)'); // Python line 146
        return compiled_graph; // Python line 147
    }
}
/**
 * Check if there are emails to process after loading.
 *
 * Python source: graph.py:150-167 (_check_has_emails_conditional)
 *
 * @param state Current workflow state
 * @returns "has_emails" if emails exist, "no_emails" to end workflow
 */
function _checkHasEmailsConditional(// Python line 150
state) {
    const total_emails = state.total_emails || 0; // Python line 160
    if (total_emails > 0) { // Python line 162
        console.info(`Found ${total_emails} emails to process`); // Python line 163
        return 'has_emails'; // Python line 164
    }
    else { // Python line 165
        console.info('No emails to process - ending workflow'); // Python line 166
        return 'no_emails'; // Python line 167
    }
}
/**
 * Node that advances to the next email.
 *
 * This is a separate node because conditional edges should not mutate state.
 *
 * Python source: graph.py:202-217 (_advance_email_node)
 *
 * @param state Current workflow state
 * @returns Updated state with incremented email index
 */
async function _advanceEmailNode(// Python line 202
state) {
    console.info(`Advancing from email ${state.current_email_index || 0} to next`); // Python line 216
    // advanceToNextEmail expects WorkflowState type but we have typeof WorkflowState.State
    // Cast to any to bridge the type mismatch between state.ts and LangGraph
    const result = advanceToNextEmail(state); // Python line 217
    return result;
}
/**
 * Conditional edge function for workflow continuation.
 *
 * This only checks if more emails exist - it does NOT advance the index.
 * The advance_email node handles incrementing the index.
 *
 * Python source: graph.py:220-240 (_check_continuation_conditional)
 *
 * @param state Current workflow state
 * @returns "continue" to advance to next email, "end" to finish workflow
 */
function _checkContinuationConditional(// Python line 220
state) {
    // Cast to bridge type mismatch between state.ts helper and LangGraph state type
    if (hasMoreEmails(state)) { // Python line 235
        console.info('More emails remain - continuing'); // Python line 236
        return 'continue'; // Python line 237
    }
    else { // Python line 238
        console.info('All emails processed - workflow complete'); // Python line 239
        return 'end'; // Python line 240
    }
}
/**
 * Get legacy LLM configuration from non-public server environment variables.
 * Browser callers should pass explicit routes instead of provider secrets.
 */
export function getLLMConfigFromEnv() {
    // In browser, use import.meta.env; in Node, use process.env
    const env = typeof import.meta !== 'undefined' ? import.meta.env || {} : globalThis.process?.env || {};
    const provider = env.VITE_LLM_PROVIDER || '';
    const model = env.VITE_LLM_MODEL || '';
    // Get API key for the selected provider
    let api_key = '';
    switch (provider.toLowerCase()) {
        case 'openai':
            api_key = env.OPENAI_API_KEY || '';
            break;
        case 'anthropic':
        case 'claude':
            api_key = env.ANTHROPIC_API_KEY || '';
            break;
        case 'google':
        case 'gemini':
            api_key = env.GOOGLE_API_KEY || '';
            break;
        case 'groq':
            api_key = env.GROQ_API_KEY || '';
            break;
        case 'deepinfra':
            api_key = env.DEEPINFRA_API_KEY || '';
            break;
        case 'mistral':
            api_key = env.MISTRAL_API_KEY || '';
            break;
        case 'xai':
        case 'grok':
            api_key = env.XAI_API_KEY || '';
            break;
        case 'ollama':
            // Ollama doesn't need an API key
            break;
    }
    return {
        provider,
        model,
        llm_config: { api_key, provider, model }
    };
}
/**
 * Create IAB Classifier with full workflow execution.
 *
 * 1:1 port of Python run_workflow() from executor.py:31-204
 *
 * Runs complete 6-node workflow with all 4 analyzer agents.
 *
 * @param options Configuration options
 * @returns Object with invoke method that executes full workflow
 */
export function createIABClassifier(options) {
    const { checkpointer, store, llm_provider, llm_model, llm_config, llm_routes, llm_route_client_factory } = options;
    const graph = buildWorkflowGraph(store, checkpointer);
    // Get config from env only for legacy callers without explicit routes.
    const hasExplicitRoutes = (!!llm_routes && Object.keys(llm_routes).length > 0) || !!llm_route_client_factory;
    const envConfig = getLLMConfigFromEnv();
    const finalProvider = llm_provider || (hasExplicitRoutes ? '' : envConfig.provider);
    const finalModel = llm_model || (hasExplicitRoutes ? '' : envConfig.model);
    const finalLLMConfig = llm_config || (hasExplicitRoutes ? {} : envConfig.llm_config);
    console.log(`📊 IAB Classifier initialized: provider=${finalProvider || 'explicit-routes'}, model=${finalModel || 'explicit-routes'}, hasApiKey=${!!finalLLMConfig.api_key}`);
    return {
        invoke: async (input, config) => {
            const timestamp = new Date().toISOString();
            const textPreview = input.text.substring(0, 200);
            console.log(`📧 Preparing to classify ${input.source}`);
            // Transform test input to workflow state format (Python: executor.py:139-151)
            // FIX Bug 2: Use input.emails if provided (for A/B testing with multiple emails)
            // Also propagate force_reprocess for re-classification scenarios
            // DEBUG: Log cost_tracker in input
            console.log(`[IABClassifier] input.cost_tracker present: ${!!input.cost_tracker}, type: ${typeof input.cost_tracker}`);
            const workflowInput = {
                user_id: input.userId,
                emails: input.emails || [{
                        id: input.sourceItemId,
                        subject: '',
                        from: '',
                        summary: input.text,
                    }],
                force_reprocess: input.force_reprocess || false,
                llm_provider: finalProvider,
                llm_model: finalModel,
                llm_config: finalLLMConfig,
                llm_routes: llm_routes ?? {},
                llm_route_client_factory,
                llm_client: input.llm_client,
                cost_tracker: input.cost_tracker, // Pass cost tracker for A/B testing
            };
            try {
                console.log('🤖 Classifying with LLM...');
                const result = await graph.invoke(workflowInput, config);
                // Extract first classification from workflow results
                // Results are in demographics_results, household_results, interests_results, purchase_results
                const allClassifications = [
                    ...(result.demographics_results || []),
                    ...(result.household_results || []),
                    ...(result.interests_results || []),
                    ...(result.purchase_results || []),
                ];
                // ===== PIPELINE SUMMARY =====
                const emailCount = workflowInput.emails.length;
                const demoCount = (result.demographics_results || []).length;
                const householdCount = (result.household_results || []).length;
                const interestsCount = (result.interests_results || []).length;
                const purchaseCount = (result.purchase_results || []).length;
                const errorCount = (result.errors || []).length;
                console.log(`\n📊 ═══════════════════════════════════════════════════════`);
                console.log(`📊 CLASSIFICATION PIPELINE SUMMARY`);
                console.log(`📊 ═══════════════════════════════════════════════════════`);
                console.log(`📊 Emails processed: ${emailCount}`);
                console.log(`📊 Demographics:     ${demoCount} classifications`);
                console.log(`📊 Household:        ${householdCount} classifications`);
                console.log(`📊 Interests:        ${interestsCount} classifications`);
                console.log(`📊 Purchase:         ${purchaseCount} classifications`);
                console.log(`📊 ───────────────────────────────────────────────────────`);
                console.log(`📊 TOTAL:            ${allClassifications.length} classifications`);
                if (errorCount > 0) {
                    console.log(`📊 ERRORS:           ${errorCount}`);
                }
                console.log(`📊 ═══════════════════════════════════════════════════════\n`);
                if (allClassifications.length === 0) {
                    throw new Error('No classifications returned from workflow');
                }
                // Use first classification (highest confidence should be first)
                const firstClassification = allClassifications[0];
                // Map IAB taxonomy to test category enum
                const category = firstClassification.value || 'OTHER';
                const confidence = firstClassification.confidence || 0.5;
                const reasoning = firstClassification.reasoning || 'No reasoning provided';
                console.log('✅ Classification complete');
                console.log(`💾 Storing classification to IndexedDBStore...`);
                // Store classification in IndexedDBStore
                const classificationKey = `${input.source}_${input.sourceItemId}`;
                await store.put([input.userId, 'iab_classifications'], classificationKey, {
                    category,
                    confidence,
                    reasoning,
                    source: input.source,
                    sourceItemId: input.sourceItemId,
                    userId: input.userId,
                    textPreview,
                    timestamp,
                });
                console.log(`💾 Stored classification for ${input.source}`);
                // Return in test-expected format with section results for A/B testing
                return {
                    success: true,
                    classification: {
                        userId: input.userId,
                        source: input.source,
                        sourceItemId: input.sourceItemId,
                        category,
                        confidence,
                        reasoning,
                        textPreview,
                        timestamp,
                    },
                    // Expose section results for A/B testing adapter
                    demographics_results: result.demographics_results || [],
                    household_results: result.household_results || [],
                    interests_results: result.interests_results || [],
                    purchase_results: result.purchase_results || [],
                };
            }
            catch (error) {
                console.error(`❌ Classification failed: ${error}`);
                return {
                    success: false,
                    error: String(error),
                };
            }
        }
    };
}
//# sourceMappingURL=index.js.map